import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { IonInput } from '@ionic/angular';
import { Subscription } from 'rxjs';
import { AJSMessagesService } from 'src/admin-front/ajs-upgraded-providers';
import { environment } from 'src/environments/environment';

import { EngagementService } from '../care/engagement/engagement.service';
import { AlcmeonContextService } from '../core/alcmeon-context/alcmeon-context.service';
import { SitesService } from '../mobile/shared/sites.service';
import { CapabilityIdEnum } from '../shared/capabilities-and-permissions';
import { SiteTypeEnum } from '../shared/enums';
import { BasicService } from '../shared/services/basic.service';
import { TaskProcessContext } from '../task-process/shared/task-process-context.class';
import { ExternalToolIntegrationModalComponent } from './external-tool-integration-modal/external-tool-integration-modal.component';
import { ExternalToolIntegrationConfig } from './external-tool-integration-modal/model';
import { ExternalUser } from './shared/models';
import { UserProfileService } from './user-profile.service';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  @Input() taskProcessContext: TaskProcessContext;
  @ViewChild('nameEditionInput') nameEditionInput: IonInput;

  user: ExternalUser;

  isEditingUserName = false;
  editedName: string;

  canOpenProfileDialog = environment.bundledWithAngularJSApp;
  canExportConversation = environment.bundledWithAngularJSApp;
  canOpenExternalTool = environment.bundledWithAngularJSApp;
  isInModal = !environment.bundledWithAngularJSApp;
  hasTagVipCapabilityOnSite = false;
  isUserReadOnly: boolean;

  externalToolConfigs: ExternalToolIntegrationConfig[] = [];
  private subscriptions: Subscription = new Subscription();

  constructor(
    private alcmeonContextService: AlcmeonContextService,
    private basicService: BasicService,
    private userProfileService: UserProfileService,
    private ajsMessagesService: AJSMessagesService,
    private sitesService: SitesService,
    private engagementService: EngagementService
  ) {
    if (this.engagementService.strategy) {
      this.subscriptions.add(
        this.engagementService.strategy.userProfileBS.subscribe(hasExternalUser => {
          this.user = null;
          this.isUserReadOnly = null;
          this.hasTagVipCapabilityOnSite = null;
          this.externalToolConfigs = [];
          if (hasExternalUser) {
            this.retrieveAndEnrichUser();
            this.checkSiteData();
          }
        })
      );
    }
  }

  ngOnInit() {
    this.canExportConversation = this.canExportConversation && !this.taskProcessContext.engagement;

    this.retrieveAndEnrichUser();
    this.checkSiteData();
  }

  private retrieveAndEnrichUser() {
    if (this.taskProcessContext?.from?.id) {
      this.userProfileService.getUserProfile(this.taskProcessContext.from.id).subscribe(user => {
        this.setUIProperties(user);
        this.user = user;
      });
    }
  }

  private checkSiteData() {
    if (this.taskProcessContext?.site?.id) {
      this.hasTagVipCapabilityOnSite = this.alcmeonContextService.hasCapability(
        this.taskProcessContext.site.id,
        CapabilityIdEnum.TagVIP
      );

      const iframeConfigs = this.alcmeonContextService.getThirdPartyCrmIFrameConfigs(this.taskProcessContext.site.id);
      this.externalToolConfigs = iframeConfigs.map(i => {
        const config: ExternalToolIntegrationConfig = { iframeUrl: i.iframe_url, buttonLabel: i.label };

        if (i.external_tab_url && !this.taskProcessContext.engagement) {
          config.externalTabUrl = i.external_tab_url
            .replace(
              '{conversationId}',
              `${this.taskProcessContext.processedTaskInfo.customFieldContext.customFieldConversationId}`
            )
            .replace('{taskId}', `${this.taskProcessContext.processedTaskId}`);
        }

        return config;
      });
      this.isUserReadOnly = this.alcmeonContextService.isLoggedUserReadOnlyForSite(this.taskProcessContext.site.id);
    }
  }

  editUserName() {
    this.isEditingUserName = true;
    this.editedName = this.taskProcessContext.from.name;
    this.nameEditionInput?.setFocus();
  }

  saveUserName() {
    this.isEditingUserName = false;
    this.taskProcessContext.from.name = this.editedName;
    const ctx = this.taskProcessContext;
    this.userProfileService.setGroupName(ctx.site.id, ctx.from.id, this.editedName).subscribe();
  }

  performUserAction(actionType: string) {
    const ctx = this.taskProcessContext;
    this.userProfileService
      .applyUserAction(ctx.site.id, ctx.processedTaskInfo.id, ctx.from.id, actionType)
      .subscribe(user => (this.user.actions_allowed = user.actions_allowed));
  }

  onVIPChanged(event: Event) {
    if (!this.taskProcessContext.from || !this.taskProcessContext.processedTaskInfo) {
      return;
    }
    const isVIP = (event as CustomEvent).detail.checked;
    this.taskProcessContext.from.isVIP = isVIP; // I run into trouble with [(ngModel)] + (ionChange)
    this.performUserAction(isVIP ? 'tag_vip' : 'untag_vip');
  }

  /**
   * @remarks will be opened only in angularjs.
   */
  openProfileDialog(organizationId = null) {
    const companyId = this.alcmeonContextService.companyId;
    const siteId = this.taskProcessContext.site.id;
    const userId = this.user.id;
    const avatarUrl = this.taskProcessContext.from.avatarURL;
    const taskId = this.taskProcessContext.engagement ? null : this.taskProcessContext.processedTaskInfo.id;
    this.ajsMessagesService.sites = this.sitesService.sitesV2;
    this.ajsMessagesService.showUserInteractions(companyId, siteId, userId, avatarUrl, taskId, organizationId);
  }

  exportConversationToExcel() {
    this.userProfileService.exportConversationToExcel(this.taskProcessContext.processedTaskId);
  }

  // required by the mobile version only
  dismissModal() {
    this.basicService.dismissModal();
  }

  async openExternalToolModal(configuration) {
    const modal = await this.basicService.modal({
      component: ExternalToolIntegrationModalComponent,
      componentProps: {
        iframeUrl: configuration.iframeUrl,
        processedTaskId: this.taskProcessContext.processedTaskId,
      },
      cssClass: 'external-tool-integration-modal',
    });
    await modal.onDidDismiss();
    this.taskProcessContext.crmIFrameDidClose$.next();
  }

  private setUIProperties(user: ExternalUser) {
    user.nameAndHandle = user.details['profile-name'];
    if (this.taskProcessContext.from?.handle) {
      user.nameAndHandle += ` \u2013 ${this.taskProcessContext.from.handle}`;
    }
    if (this.taskProcessContext.site.type === SiteTypeEnum.Facebook) {
      // seems like the URL is not working for FB profiles
      user.external_url = null;
    }
  }
  ngOnDestroy(): void {
    this.subscriptions?.unsubscribe();
  }
}
